import { Dialog, DialogActions, DialogContent, DialogTitle, Paper, PaperProps } from '@mui/material';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';
import 'swiper/scss';
import 'swiper/scss/navigation';
import 'swiper/scss/pagination';
import Button from '../../../../../../components/Button/Button';
import Input from '../../../../../../components/Input/Input';
import { useAppDispatch, useAppSelector } from '../../../../../../reduxService/hooks';
import { EConversionImperialToSI, EConversionSIToImperial, conversionLabel, convertTo } from '../../../../../conversion';
import { MaterialCategory, MaterialType, Usage, isConcrete } from '../../../../../enum/Material';
import { fetchMaterialsByType, fetchStillingBasingMaterials, selectMaterial } from '../../../../../store/material/materialSlice';
import { IMaterialSectorSlice, IProjectMacra1, setMaterialToSector, setMaterialToSectors, setMaterialToSubSectors } from '../../../../store/project/projectSlice';
import { MaterialCategoryFactory } from '../MaterialCategory/MaterialCategoryFactory';
import { MaterialList } from '../MaterialDropdowns/MaterialList';
import { MaterialTypeDropdown } from '../MaterialDropdowns/MaterialTypeDropdown';
import { MaterialSectorFormik } from '../MaterialSectorFormik/MaterialSectorFormik';
import { MaterialSwiper } from '../MaterialSwiper/MaterialSwiper';
import { IFormikMaterialProps } from './IModalMaterial';
import './ModalMaterial.scss';
import { roundToFour, roundToTwo } from '../../Geometry/utils';
import { cleanResults, setDischargeAlert } from '../../../../store/project/resultsSlice';
import { get } from 'lodash';
import { IMaterialResponse, IPhysicalDataResponse } from '../../../../../api/IApiResponse';
import { selectProjectInfo } from '../../../../../store/projectInfo/projectInfoSlice';
import { ISectorResponse } from '../../../../api/IApiResponse';
import * as Alert from '../../../../../../components/Alert/Alert';
import { getActionByProjectType } from '../../../../../actionMap';
import { IProjectMacra2 } from '../../../../../MACRA_2/store/project/projectSlice';
import { IInitialState } from '../../../../../../reduxService';
import { UnitOfMeasure } from '../../../../../enum/project';

export const PaperComponent = (props: PaperProps) => {
    return (
        <Draggable handle='#draggable-dialog-title' cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} />
        </Draggable>
    );
};

export interface ISector {
    [key: number]: IFormikSector;
}
export interface IFormikSector {
    vegetatedAllowShearStress?: number;
    isCastoro?: boolean;
    isDeleted?: boolean;
    isGeotextileFilter?: boolean;
    isXties?: boolean;
    isAllowedChangeCheckbox?: boolean;
    physicalData?: IPhysicalDataResponse[];
    vegetatedRoughness?: number;
    roughness?: number;
    category?: MaterialCategory;
    materialId?: number;
    allowShearStress?: number;
    combinationPD?: number;
    rockD50?: number;
    thickness?: number;
    cu?: number;
    material?: IMaterialResponse;
    rf?: number;
    time?: number;
    isVegetated?: boolean;
}

export interface IFormikForm {
    sectorChannel: number;
    allowShearStress: number;
    roughness: number;
    vegetatedAllowShearStress?: number;
    vegetatedRoughness?: number;
    materialId?: number;
    [key: number]: IFormikSector;
    isDefault?: boolean;
    isForAll?: boolean;
    hideIsForAll?: boolean;
}

const FormikMaterial = (props: IFormikMaterialProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const materials = useAppSelector(selectMaterial) as IInitialState<IMaterialResponse[]>;
    const { sectorChannel, closeModal, isMacra2, showSetMatForAll } = props;
    const projectInfo = useAppSelector(selectProjectInfo);
    let isImperial = projectInfo?.data?.unitOfMeasure == UnitOfMeasure.Imperial;
    const { selectProject } = getActionByProjectType();
    const project: IInitialState<IProjectMacra1 | IProjectMacra2> = useAppSelector(selectProject as any);
    const [init, setInit] = useState(false);
    const [formikInitialValue, setFormikInitialValue] = useState<IFormikForm>();
    const [hideMat, setHideMat] = useState<boolean>(false);

    useEffect(() => {
        if (hideMat) {
            setHideMat(false);
        }
    }, [hideMat]);

    //Macra1
    useEffect(() => {
        if (!isMacra2) {
            let matType = get(project, 'data.geometry[0].material.type', MaterialType.ProtectionSystems);
            dispatch(fetchMaterialsByType(matType));
        } else {
            dispatch(fetchStillingBasingMaterials());
        }
    }, []);

    useEffect(() => {
        if (!init && !!materials.data && !isMacra2) {
            let sectors: ISector = {};
            if (!!props.material && !!props.sectorChannel) {
                let material = props.material;
                let formikPhysicalData: any = get(material, 'physicalData[0]');
                sectors[props.sectorChannel] = {
                    material: material,
                    isCastoro: material.isCastoro,
                    isAllowedChangeCheckbox: material.isAllowedChangeCheckbox,
                    isDeleted: material.isDeleted,
                    isXties: material.isXties,
                    isGeotextileFilter: material.isGeotextileFilter,
                    physicalData: material.physicalData ? [...material.physicalData] : [],
                    isVegetated: material.isVegetated,
                    vegetatedRoughness: formikPhysicalData?.vegetatedRoughness,
                    roughness: formikPhysicalData?.roughness,
                    allowShearStress: formikPhysicalData?.allowShearStress,
                    vegetatedAllowShearStress: formikPhysicalData?.vegetatedAllowShearStress,
                    category: material.category,
                    materialId: material.baseMaterialId || material.id,
                    combinationPD: 0,
                    rockD50: formikPhysicalData?.rockD50,
                    thickness: formikPhysicalData?.thickness,
                    cu: formikPhysicalData?.cu,
                    rf: formikPhysicalData?.rf,
                    time: formikPhysicalData?.time,
                };
            } else {
                project.data?.geometry &&
                    (project.data?.geometry as ISectorResponse[]).forEach((data: ISectorResponse) => {
                        let field = data.channel;
                        let usage = data.material?.usage;
                        let idToSearch = usage === Usage.Base ? data.material?.id : data.material?.baseMaterialId;
                        let material: IMaterialResponse | undefined = materials.data?.find((material: IMaterialResponse) => material.id === idToSearch);
                        if (!!data.material && !!data.material.id) {
                            material = { ...material, ...data.material, images: material?.images };
                        }
                        if (!!material) {
                            let formikPhysicalData;
                            if (!!material.physicalData) {
                                if (material.physicalData.length > 1) {
                                    let defaultData = material.physicalData.find(data => data.isDefault);
                                    if (!!defaultData) {
                                        formikPhysicalData = defaultData;
                                    } else {
                                        formikPhysicalData = material.physicalData[0];
                                    }
                                } else {
                                    formikPhysicalData = material.physicalData[0];
                                }
                            }

                            sectors[field] = {
                                material: material,
                                isCastoro: material.isCastoro,
                                isAllowedChangeCheckbox: material.isAllowedChangeCheckbox,
                                isDeleted: material.isDeleted,
                                isXties: material.isXties,
                                isGeotextileFilter: material.isGeotextileFilter,
                                physicalData: material.physicalData ? [...material.physicalData] : [],
                                isVegetated: material.isVegetated,
                                vegetatedRoughness: formikPhysicalData?.vegetatedRoughness,
                                roughness: formikPhysicalData?.roughness,
                                allowShearStress: formikPhysicalData?.allowShearStress,
                                vegetatedAllowShearStress: formikPhysicalData?.vegetatedAllowShearStress,
                                category: material.category,
                                materialId: material.baseMaterialId || material.id,
                                combinationPD: 0,
                                rockD50: formikPhysicalData?.rockD50,
                                thickness: formikPhysicalData?.thickness,
                                cu: formikPhysicalData?.cu,
                                rf: formikPhysicalData?.rf,
                                time: formikPhysicalData?.time,
                            };
                        } else {
                            sectors[field] = { ...data };
                        }
                    });
            }
            let allowShearStress, roughness;
            if (props.sectorChannel) {
                allowShearStress = sectors[props.sectorChannel].allowShearStress;
                roughness = sectors[props.sectorChannel].roughness;
            } else {
                allowShearStress = sectors[3].allowShearStress || 0;
                roughness = sectors[3].roughness || 0;
            }
            const data = {
                sectorChannel: props.sectorChannel ?? 3,
                allowShearStress: allowShearStress || 0,
                roughness: roughness || 0,
                materialId: undefined,
                isDefault: true,
                isForAll: false,
                hideIsForAll: !showSetMatForAll,
                ...sectors,
            };
            setFormikInitialValue(data);
            setInit(true);
        }
    }, [materials, init]);

    useEffect(() => {
        if (!init && isMacra2 && !!materials.data) {
            let material: IMaterialResponse = (project!.data! as IProjectMacra2).stillingBasin!.material;
            let formikPhysicalData: any = get(project, 'data.stillingBasin.material.physicalData[0]', {});
            let usage = material?.usage;
            let idToSearch = usage === Usage.Base ? material?.id : material?.baseMaterialId;
            let findedMat: IMaterialResponse | undefined = materials.data?.find((material: IMaterialResponse) => material.id === idToSearch);
            let newMat;
            if (!!findedMat) {
                if (!!findedMat.physicalData) {
                    if (findedMat.physicalData.length > 1) {
                        newMat = {
                            ...material,
                            physicalData: findedMat.physicalData.map(data => {
                                if (((!!data.cu && data.cu === formikPhysicalData.cu) || !data.cu) && data.rockD50 === formikPhysicalData.rockD50 && data.thickness === formikPhysicalData.thickness) {
                                    return { ...formikPhysicalData, isDefault: true };
                                } else {
                                    return { ...data, isDefault: false };
                                }
                            }),
                        };
                    }
                }
            }

            const data = {
                3: {
                    material: newMat ?? material,
                    isCastoro: material.isCastoro,
                    isXties: material.isXties,
                    isGeotextileFilter: material.isGeotextileFilter,
                    physicalData: material.physicalData ? [...material.physicalData] : [],
                    isVegetated: material.isVegetated,
                    vegetatedRoughness: formikPhysicalData?.vegetatedRoughness,
                    roughness: formikPhysicalData?.roughness,
                    allowShearStress: formikPhysicalData?.allowShearStress,
                    vegetatedAllowShearStress: formikPhysicalData?.vegetatedAllowShearStress,
                    category: material.category,
                    materialId: material.baseMaterialId || material.id,
                    combinationPD: 0,
                    rockD50: formikPhysicalData?.rockD50,
                    thickness: formikPhysicalData?.thickness,
                    cu: formikPhysicalData?.cu,
                    rf: formikPhysicalData?.rf,
                    time: formikPhysicalData?.time,
                },
                sectorChannel: 3,
                allowShearStress: formikPhysicalData?.allowShearStress,
                roughness: formikPhysicalData?.roughness,
                materialId: material.baseMaterialId || material.id,
                isDefault: true,
                isForAll: false,
                hideIsForAll: !showSetMatForAll,
            };
            setFormikInitialValue(data);
            setInit(true);
        }
    }, [materials, init]);
    return (
        <>
            {formikInitialValue && (
                <Dialog open={props.open} onClose={() => closeModal()} PaperComponent={PaperComponent} aria-labelledby='draggable-dialog-title' className='dialog-material'>
                    <Formik
                        enableReinitialize
                        initialValues={formikInitialValue}
                        validate={values => {
                            const errors: any = {};
                            return errors;
                        }}
                        onSubmit={(values, actions) => {
                            const sectData = values[values.sectorChannel];
                            let selectedPhysicalData = sectData.physicalData!.find(pd => pd.id === sectData.combinationPD);
                            if (!selectedPhysicalData) selectedPhysicalData = sectData.physicalData!.find(pd => pd.isDefault);
                            let rockD50 = sectData.rockD50 ? sectData.rockD50 : selectedPhysicalData?.rockD50;
                            let thickness = sectData.thickness ? sectData.thickness : selectedPhysicalData?.thickness;
                            let cu = sectData.cu ? sectData.cu : selectedPhysicalData?.cu;

                            let newPhysicalData: IPhysicalDataResponse = {
                                ...selectedPhysicalData!,

                                rf: sectData.rf,
                                time: sectData.time,
                                roughness: sectData.roughness,
                                thickness: thickness,
                                rockD50: rockD50,
                                cu: cu,
                                allowShearStress: sectData.allowShearStress,
                                vegetatedRoughness: sectData.vegetatedRoughness,
                                vegetatedAllowShearStress: sectData.vegetatedAllowShearStress ?? selectedPhysicalData?.vegetatedAllowShearStress,
                            };
                            let mat = sectData.material;
                            if (!!mat) {
                                actions.setFieldValue('roughness', sectData.roughness);
                                actions.setFieldValue('allowShearStress', sectData.allowShearStress);
                                actions.setFieldValue('vegetatedRoughness', sectData.vegetatedRoughness);
                                actions.setFieldValue('vegetatedAllowShearStress', sectData.vegetatedAllowShearStress);
                                let category = sectData.category ? sectData.category : mat.category;

                                let materialSlice: IMaterialSectorSlice = {
                                    sector: typeof values.sectorChannel === 'string' ? parseInt(values.sectorChannel) : values.sectorChannel,
                                    materialId: sectData.materialId ? sectData.materialId : 0,

                                    material: {
                                        ...mat,
                                        isGeotextileFilter: sectData.material?.isGeotextileFilter ? sectData.material.isGeotextileFilter : false,
                                        isXties: sectData.material?.isXties ? sectData.material.isXties : false,
                                        isCastoro: sectData.material?.isCastoro ? sectData.material.isCastoro : false,
                                        isVegetated: !!sectData.isVegetated ? sectData.isVegetated : false,
                                        physicalData: [newPhysicalData],
                                        category,
                                    },
                                };
                                if (!sectorChannel && !isMacra2) {
                                    dispatch(cleanResults());
                                    if (values.isForAll) {
                                        dispatch(setMaterialToSectors(materialSlice));
                                        dispatch(setMaterialToSubSectors(materialSlice));
                                    } else {
                                        dispatch(setMaterialToSector(materialSlice));
                                    }
                                    dispatch(setDischargeAlert(false));

                                    Alert.toastSuccess(t('modal_material_saved'));
                                } else {
                                    if (props.onClose) {
                                        props.onClose(materialSlice.material);
                                        if (isMacra2) {
                                            Alert.toastSuccess(t('modal_material_saved'));
                                        }
                                    }
                                }
                            }
                            //for subs
                            if (props.materialId && props.sectorChannel && props.material) {
                                closeModal();
                            }
                        }}>
                        {({ values, handleSubmit }) => {
                            let sectorMaterial;
                            if (!isMacra2) sectorMaterial = (project.data?.geometry as ISectorResponse[])?.find(sec => sec.channel === values.sectorChannel)?.material;
                            let localStress = 0;
                            let localRough = 0;
                            if (!!sectorMaterial && !!sectorMaterial.physicalData?.length && sectorMaterial.physicalData.length > 0) {
                                localStress = sectorMaterial.isVegetated
                                    ? roundToTwo(sectorMaterial.physicalData[0].vegetatedAllowShearStress ?? 0)
                                    : roundToTwo(sectorMaterial.physicalData[0].allowShearStress ?? 0);
                                localRough = sectorMaterial.isVegetated
                                    ? roundToFour(sectorMaterial.physicalData[0].vegetatedRoughness ?? 0)
                                    : roundToFour(sectorMaterial?.physicalData[0].roughness ?? 0);
                            }
                            if (isMacra2) {
                                localStress = values.allowShearStress;
                                localRough = values.roughness;
                            }

                            return (
                                <Form noValidate>
                                    <div className='ModalMaterial'>
                                        <DialogTitle style={{ cursor: 'move' }} id='draggable-dialog-title'>
                                            {t('modal_material_title')}
                                            {/* <UpdatedMaterial /> */}
                                        </DialogTitle>
                                        <DialogContent>
                                            <div className='d-flex justify-content-between gap-2 pb-3'>
                                                {!isMacra2 && <MaterialTypeDropdown isMacra2={isMacra2} />}
                                                {!isMacra2 && <MaterialSectorFormik sectorChannel={sectorChannel} set={() => setHideMat(true)} />}
                                                {!isConcrete(values[values.sectorChannel]?.category ?? sectorMaterial?.category) && (
                                                    <Input
                                                        type='number'
                                                        step={0.01}
                                                        title={t('material_shear_stress', { unit_measure: conversionLabel.Nm2_lbfft2[projectInfo.data?.unitOfMeasure as number] })}
                                                        className='d-flex flex-column'
                                                        name='stress'
                                                        disabled
                                                        value={isImperial ? roundToTwo(convertTo(localStress, EConversionSIToImperial.Nm2_to_lbfft2)) : localStress}
                                                    />
                                                )}

                                                <Input type='number' value={localRough} step={0.0001} title={t('material_roughness')} className='d-flex flex-column' name='roughness' disabled />
                                            </div>

                                            <div className='d-flex content-wrapper gap-2 w-100'>
                                                <MaterialList />
                                                <div className='w-65 d-flex flex-column gap-2'>
                                                    <MaterialSwiper />
                                                    {!hideMat && <MaterialCategoryFactory />}
                                                </div>
                                            </div>
                                        </DialogContent>
                                    </div>
                                    <DialogActions>
                                        <Button className='me-2' classNameBtn='btn-cancel' onClick={() => closeModal()}>
                                            {t('cancel_button')}
                                        </Button>
                                        <Button classNameBtn='btn-apply' type='submit' onClick={() => handleSubmit()}>
                                            {t('apply_button')}
                                        </Button>
                                    </DialogActions>
                                </Form>
                            );
                        }}
                    </Formik>
                </Dialog>
            )}
        </>
    );
};

export default FormikMaterial;
